import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Header from '../../components/ui/Header';
import ProfileHeader from './components/ProfileHeader';
import ProfileInformation from './components/ProfileInformation';
import GamingHistory from './components/GamingHistory';
import SubscriptionSettings from './components/SubscriptionSettings';
import PrivacySettings from './components/PrivacySettings';
import SecuritySettings from './components/SecuritySettings';
import StudioApplication from './components/StudioApplication';
import SupportTickets from './components/SupportTickets';
import FriendsAndSocial from './components/FriendsAndSocial';
import AchievementsTab from './components/AchievementsTab';
import Icon from '../../components/AppIcon';
import OrderHistory from './components/OrderHistory';
import { supabase } from '../../utils/supabase';

const UserProfileSettings = () => {
  const { user: authUser, userProfile, loading: authLoading, updateProfile } = useAuth();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('profile');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Handle URL query parameters for tab navigation
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    if (tab && ['profile', 'friends', 'achievements', 'gaming', 'subscription', 'orders', 'support', 'privacy', 'security', 'studio'].includes(tab)) {
      setActiveTab(tab);
    }
  }, [location.search]);

  // Real user data from auth and profile
  const [user, setUser] = useState(null);
  const [gamingData, setGamingData] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [privacyData, setPrivacyData] = useState({
    profileVisibility: "public",
    showActivity: true,
    showOnlineStatus: true,
    allowFriendRequests: true,
    personalizedRecommendations: true,
    usageAnalytics: true,
    performanceData: false,
    emailNotifications: "important",
    marketingEmails: false,
    gameUpdates: true,
    socialNotifications: true,
    twoFactorEnabled: true,
    loginNotifications: true
  });
  const [securityData, setSecurityData] = useState({
    lastPasswordChange: null,
    twoFactorEnabled: false,
    activeSessions: 1
  });

  // Helper function to get avatar URL from auth user or profile
  const getAvatarUrl = () => {
    // First check if user has uploaded a custom avatar
    if (userProfile?.avatar_url) {
      return userProfile.avatar_url;
    }

    // Then check if they signed in with Google and have a profile picture
    if (authUser?.user_metadata?.avatar_url) {
      return authUser.user_metadata.avatar_url;
    }

    // Fallback to a default avatar
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(userProfile?.full_name || authUser?.email || 'User')}&background=6366f1&color=fff&size=150`;
  };

  // Helper function to format join date
  const formatJoinDate = (dateString) => {
    if (!dateString) return 'Recently';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long'
    });
  };

  useEffect(() => {
    const loadUserData = async () => {
      if (!authUser || !userProfile || authLoading) return;

      try {
        setLoading(true);
        setError(null);

        // Load gaming statistics
        const { data: gamingSessions, error: sessionsError } = await supabase
          .from('user_game_sessions')
          .select('duration_minutes, game_id')
          .eq('user_id', authUser.id);

        if (sessionsError) {
          console.error('Error loading gaming sessions:', sessionsError);
        }

        // Load user ratings/achievements
        const { data: userRatings, error: ratingsError } = await supabase
          .from('user_game_ratings')
          .select('*')
          .eq('user_id', authUser.id);

        if (ratingsError) {
          console.error('Error loading user ratings:', ratingsError);
        }

        // Load subscription data
        const { data: subscriptionData, error: subError } = await supabase
          .from('subscriptions')
          .select('*')
          .eq('user_id', authUser.id)
          .eq('status', 'active')
          .single();

        if (subError && subError.code !== 'PGRST116') { // PGRST116 is "no rows returned"
          console.error('Error loading subscription:', subError);
        }

        // Calculate gaming statistics
        const totalPlaytime = gamingSessions?.reduce((total, session) => total + (session.duration_minutes || 0), 0) || 0;
        const totalGames = new Set(gamingSessions?.map(session => session.game_id) || []).size;
        const achievements = userRatings?.length || 0;
        const avgSession = gamingSessions?.length > 0 ? totalPlaytime / gamingSessions.length : 0;

        // Set user data
        setUser({
          id: authUser.id,
          name: userProfile.full_name || authUser.email?.split('@')[0] || 'User',
          email: authUser.email,
          username: userProfile.full_name?.toLowerCase().replace(/\s+/g, '') || authUser.email?.split('@')[0] || 'user',
          avatar: getAvatarUrl(),
          bio: userProfile.bio || '',
          location: userProfile.location || '',
          timezone: userProfile.timezone || 'UTC+0',
          language: userProfile.language || 'en',
          joinDate: formatJoinDate(userProfile.created_at),
          totalPlaytime: Math.round(totalPlaytime / 60), // Convert to hours
          achievements,
          subscriptionStatus: subscriptionData?.status || 'inactive'
        });

        setGamingData({
          totalGames,
          totalPlaytime: Math.round(totalPlaytime / 60), // Convert to hours
          achievements,
          avgSession: Math.round(avgSession * 10) / 10 // Round to 1 decimal
        });

        // Set subscription data
        if (subscriptionData) {
          const planNames = {
            basic: 'Basic',
            premium: 'Premium',
            studio: 'Studio'
          };

          const planFeatures = {
            basic: ['Access to 100+ games', 'HD streaming', '1 device'],
            premium: ['Access to 500+ games', '4K streaming', 'Up to 3 devices', 'Early access'],
            studio: ['All Premium features', 'Game publishing tools', 'Revenue analytics', 'Priority support']
          };

          setSubscription({
            plan: subscriptionData.plan,
            planName: planNames[subscriptionData.plan] || subscriptionData.plan,
            price: subscriptionData.price_per_month,
            status: subscriptionData.status,
            nextBilling: subscriptionData.current_period_end ? new Date(subscriptionData.current_period_end).toISOString().split('T')[0] : null,
            features: planFeatures[subscriptionData.plan] || []
          });
        } else {
          setSubscription({
            plan: 'none',
            planName: 'No Active Subscription',
            price: 0,
            status: 'inactive',
            nextBilling: null,
            features: []
          });
        }

      } catch (error) {
        console.error('Failed to load user data:', error);
        setError('Failed to load profile data. Please try refreshing the page.');
      } finally {
        setLoading(false);
      }
    };

    loadUserData();
  }, [authUser, userProfile, authLoading]);

  const handleUserUpdate = (updatedData) => {
    setUser(prev => ({ ...prev, ...updatedData }));
    // Reload user data to get fresh data from the database
    if (authUser && userProfile) {
      // Trigger a re-fetch by updating a dependency
      window.location.reload();
    }
  };

  const handleAvatarUpdate = (newAvatarUrl) => {
    setUser(prev => ({ ...prev, avatar: newAvatarUrl }));
  };

  const handlePrivacyUpdate = (updatedPrivacy) => {
    setPrivacyData(updatedPrivacy);
  };

  const tabs = [
    { id: 'profile', label: 'Profile', icon: 'User' },
    { id: 'friends', label: 'Friends', icon: 'Users' },
    { id: 'achievements', label: 'Achievements', icon: 'Award' },
    { id: 'gaming', label: 'Gaming History', icon: 'Gamepad2' },
    { id: 'subscription', label: 'Subscription', icon: 'CreditCard' },
    { id: 'orders', label: 'Order History', icon: 'ShoppingBag' },
    { id: 'support', label: 'Support Tickets', icon: 'MessageSquare' },
    { id: 'privacy', label: 'Privacy', icon: 'Shield' },
    { id: 'security', label: 'Security', icon: 'Lock' },
    { id: 'studio', label: 'Studio', icon: 'Building2' }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'profile':
        return (
          <ProfileInformation
            user={user}
            onUpdate={handleUserUpdate}
          />
        );
      case 'friends':
        const params = new URLSearchParams(location.search);
        const subTab = params.get('sub');
        return (
          <FriendsAndSocial initialTab={subTab} />
        );
      case 'achievements':
        return <AchievementsTab />;
      case 'gaming':
        return (
          <GamingHistory
            gamingData={gamingData}
          />
        );
      case 'subscription':
        return (
          <SubscriptionSettings
            subscription={subscription}
          />
        );
      case 'orders':
        return <OrderHistory />;
      case 'support':
        return (
          <SupportTickets />
        );
      case 'privacy':
        return (
          <PrivacySettings
            privacyData={privacyData}
            onUpdate={handlePrivacyUpdate}
          />
        );
      case 'security':
        return (
          <SecuritySettings
            securityData={securityData}
          />
        );
      case 'studio':
        return (
          <StudioApplication
            userProfile={userProfile}
          />
        );
      default:
        return null;
    }
  };

  // Show loading state
  if (authLoading || loading || !user) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-16 pb-20 lg:pb-8">
          <div className="container mx-auto px-4 py-8">
            <div className="flex items-center justify-center min-h-[400px]">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                <p className="text-muted-foreground">Loading your profile...</p>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-16 pb-20 lg:pb-8">
          <div className="container mx-auto px-4 py-8">
            <div className="flex items-center justify-center min-h-[400px]">
              <div className="text-center">
                <div className="text-red-500 mb-4">
                  <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 18.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-2">Error Loading Profile</h3>
                <p className="text-muted-foreground mb-4">{error}</p>
                <button
                  onClick={() => window.location.reload()}
                  className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
                >
                  Try Again
                </button>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <main className="pt-16 pb-20 lg:pb-8">
        <div className="container mx-auto px-4 py-8">
          {/* Profile Header */}
          <div className="mb-8">
            <ProfileHeader
              user={user}
              onAvatarUpdate={handleAvatarUpdate}
            />
          </div>

          {/* Desktop Layout */}
          <div className="hidden lg:flex gap-8">
            {/* Sidebar Navigation */}
            <div className="w-64 flex-shrink-0">
              <div className="bg-card rounded-lg border border-border p-4 sticky top-24">
                <nav className="space-y-2">
                  {tabs.map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === tab.id
                          ? 'bg-primary/10 text-primary border border-primary/20' : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                        }`}
                    >
                      <Icon
                        name={tab.icon}
                        size={20}
                        className={activeTab === tab.id ? 'text-primary' : 'text-muted-foreground'}
                      />
                      <span>{tab.label}</span>
                    </button>
                  ))}
                </nav>
              </div>
            </div>

            {/* Main Content */}
            <div className="flex-1">
              {renderTabContent()}
            </div>
          </div>

          {/* Mobile Layout */}
          <div className="lg:hidden">
            {/* Mobile Tab Navigation */}
            <div className="bg-card rounded-lg border border-border mb-6">
              <div className="flex overflow-x-auto">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex-shrink-0 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === tab.id
                        ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'
                      }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Mobile Content */}
            <div>
              {renderTabContent()}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default UserProfileSettings;
